import type { ColorSource, DestroyOptions } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { PopupRays } from './components/PopupRays';
import { ColorOverlay } from '../game/views/common/ColorOverlay';
import { FXParticleLayer } from '../libs/particles/FXParticleLayer';
import { registerResizableObject } from '../utils/pixi/registerResizableObject';
import type { Popup } from './Popup';

const popupPadding = { horizontal: 60, vertical: 200 };

export class PopupStackView extends Container {
  public readonly overlay = new ColorOverlay({
    fillColor: 'black',
    fillOpacity: 0.6,
    anchor: { x: 0.5, y: 0.5 },
  }).props({
    parent: this,
  });

  private readonly flashOverlay = new ColorOverlay({
    fillColor: 'white',
    fillOpacity: 0.25,
    anchor: { x: 0.5, y: 0.5 },
  }).props({
    parent: this,
  });

  public readonly particleLayer: FXParticleLayer = new FXParticleLayer().props({
    parent: this,
  });

  public readonly rays = new PopupRays().props({
    parent: this,
  });

  public readonly popupContainer = new Container().props({
    parent: this,
  });

  public autoOverlayFading = true;

  private readonly viewSize: { width: number; height: number } = { width: 780, height: 1280 };

  public readonly onResized = new Signal<(width: number, height: number) => void>();

  constructor() {
    super();
    registerResizableObject(this);
  }

  public destroy(options?: DestroyOptions): void {
    super.destroy(options);
    this.onResized.disconnectAll();
  }

  public fadeInBackgroundElements(hideRays: boolean, overlayFillOpacity = 0.6): void {
    const { overlay, rays, autoOverlayFading } = this;

    overlay.setFillOpacity(overlayFillOpacity);
    if (autoOverlayFading) overlay.fadeIn();

    if (hideRays) rays.fadeOut();
    else rays.fadeIn();
  }

  public fadeOutBackgroundElements(): void {
    const { overlay, rays, autoOverlayFading } = this;

    if (autoOverlayFading) overlay.fadeOut();
    rays.fadeOut();
  }

  public flash(duration = 250, delay = 0, tint: ColorSource = 0xffb508): void {
    this.flashOverlay.flash(duration, delay);
    this.flashOverlay.tint = tint;
  }

  public update(deltaTime: number): void {
    this.rays.update(deltaTime);
    this.particleLayer.update(deltaTime);
  }

  public clearParticles(): void {
    this.particleLayer.clear();
  }

  public resize(width: number, height: number): void {
    const { viewSize, particleLayer } = this;

    this.x = Math.floor(width / 2);
    this.y = Math.floor(height / 2);

    particleLayer.x = Math.floor(-width / 2);
    particleLayer.y = Math.floor(-height / 2);

    viewSize.width = width;
    viewSize.height = height;

    this.onResized.emit(width, height);
  }

  public alignPopups(popups: Popup[]): void {
    const { width, height } = this.viewSize;

    popups.forEach((popup): void => {
      const { root, initialBounds } = popup;

      if (initialBounds) {
        const scale = Math.min(
          height / (initialBounds.height + popupPadding.vertical),
          width / (initialBounds.width + popupPadding.horizontal),
        );
        root.scale.set(scale);
        root.x = (-initialBounds.width * scale) / 2;
        root.y = (-initialBounds.height * scale) / 2;
      }

      popup?.resize?.();
    });
  }
}
